import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { useTogglingSelectAll } from '@/search/hooks/useTogglingSelectAll.hook';
import { ToggleSelectAll } from '@/search/multiSelect/ToggleSelectAll.molecule';
import { SelectAndAdd } from '@/search/multiSelect/SelectAndAdd.molecule';
import { useTranslation } from 'react-i18next';
import { isAsset, isDisplay } from '@/utilities/utilities';
import { doTrack } from '@/track/track.service';
import { SearchPanes } from '@/search/search.constants';
import { getStoreForPane, toggleSelectAllItems } from '@/search/search.actions';
import { batchAddItems } from '@/trendData/trend.actions';

interface TrendMultiSelectIF {
  pane: SearchPanes;
  items: any[];
}

/**
 * Manages trend multi-select
 */
export const TrendMultiSelect: React.FunctionComponent<TrendMultiSelectIF> = ({ pane, items }) => {
  const { t } = useTranslation();

  const store = getStoreForPane(pane);
  const selectAllChecked = useFluxPath(store, () => store.selectAllChecked);
  const selectedItemIds = useFluxPath(store, () => store.selectedItemIds);

  const options = [
    { value: 'separate', label: t('MULTI_SELECT.TREND.SEPARATE') },
    { value: 'same', label: t('MULTI_SELECT.TREND.SAME') },
  ];

  const selectableItems = useMemo(
    () => items?.filter((item) => !isAsset(item) && !isDisplay(item) && !item.hasChildren),
    [items],
  );
  const { togglingSelectAll, setTogglingSelectAll } = useTogglingSelectAll({
    items: selectableItems,
  });
  const [selectedValue, setSelectedValue] = useState(options[0]);

  useEffect(() => {
    setSelectedValue(selectedValue.value === 'separate' ? options[0] : options[1]);
  }, [options[0].label]);

  const isSelectAllDisabled = selectableItems.length === 0;

  return (
    <div className="flexColumnContainer flexAlignCenter" data-testid="multiSelectActions">
      <ToggleSelectAll
        togglingSelectAll={togglingSelectAll}
        isChecked={selectAllChecked}
        onClick={() => {
          setTogglingSelectAll(true);
          toggleSelectAllItems(selectableItems);
        }}
        isDisabled={isSelectAllDisabled}
      />
      <SelectAndAdd
        selectedItemIds={selectedItemIds}
        options={options}
        onChange={setSelectedValue}
        selectedValue={selectedValue}
        onAddClick={() => {
          const selectedItems = selectedItemIds
            .map((id: string) => _.find(selectableItems, { id }))
            .filter((item: string) => !!item);
          if (_.get(selectedValue, 'value', '') === 'separate') {
            batchAddItems(selectedItems);
            doTrack('Multi-Select', 'Add items to separate lanes');
          } else {
            batchAddItems(selectedItems, false);
            doTrack('Multi-Select', 'Add items to same lane and axis');
          }
        }}
        isSelectAllDisabled={isSelectAllDisabled}
      />
    </div>
  );
};
